import { useCallback, useRef } from 'react';
import { EventType } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-call/v1/call_pops.pb';
import { CountUnreadVoicemailsResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  VoicemailEventType,
  VoicemailUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { PhoneEventsUser } from '@wetags/phone-events';
import { produce } from 'immer';
import { QueryClient, useQueryClient } from 'react-query';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { NotificationQueries } from '@frontend/api-notifications';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { getDisplayName } from '@frontend/get-display-name';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { usePhotoStore } from '@frontend/photos';
import { CallPopInterface, useCallPopStateSync } from '@frontend/pop';
import { useAppScopeStore } from '@frontend/scope';
import { phoneNotificationTypes } from '@frontend/settings-routing';
import TempoTracing from '@frontend/tempo-tracing';
import { sentry } from '@frontend/tracking';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useCallQueueMetricsStore } from './components/call-queue-stats/use-metrics-store';
import { useCallRecordEvent } from './components/recent-calls/call-record-update';
import { useExportCallRecordUpdater } from './components/recent-calls/export-call-record-update';
import { useVoicemailEventEmitter } from './voicemail-event-emitter';

type Props = { enabled: boolean };
export const useWebsocketDeskPhoneEventSubscription = ({ enabled }: Props) => {
  const { notifications, addNotification } = useCallPopStateSync();
  const [isCallPopTracingEnabled] = PhoneEventsUser.useWetag('fe-call-pop-trace');
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const handleGetPicture = usePhotoStore((state) => state.handleGetPicture);

  // This is meant to track the trace for an inbound call through the life of it (until answer, reject or hangup) even in between various WS events and user interactions
  const currentInboundTrace = useRef<string | undefined>(undefined);

  const { data: notificationSettings } = NotificationQueries.useNotificationSettingsQuery();
  const CallerProfileAutoOpenNotificationId = phoneNotificationTypes.find(
    (notification) => notification.type === 'incoming-call'
  )?.id;

  const autoShowCallerProfile = !!notificationSettings?.find(({ id }) => id === CallerProfileAutoOpenNotificationId)
    ?.sendNotification;

  const handlerV2 = useCallback<GetWebsocketEventHandler<'PhoneSystemEventsV2'>>(
    async (payload) => {
      const allNotifications = notifications;
      let traceId: string | undefined;
      let event = 'unknown';
      try {
        event = payload.params.event || 'unknown';
        traceId = payload.trace_id;

        switch (event) {
          case 'inbound_call': {
            if (!!traceId && isCallPopTracingEnabled) {
              TempoTracing.continueTrace(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan(event), {
                parentSpanName: TempoTracing.spanNameGenerators.websocketSpan(),
              });

              if (!enabled && !!traceId) {
                TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan(event), {
                  eventMessage: 'Call pop notifications for deskphone are disabled',
                  eventType: EventType.EVENT_TYPE_INFO,
                  timestamp: new Date().toISOString(),
                });
                TempoTracing.endSpan(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan(event));
                return;
              }

              currentInboundTrace.current = traceId;
              TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan(event), {
                eventMessage: 'Showing call pop',
                eventType: EventType.EVENT_TYPE_INFO,
                timestamp: new Date().toISOString(),
              });
            }

            const contacts = payload.params.contact_matches
              ? payload.params.contact_matches
                  .filter((contact) => (demoSourceIds.length > 0 ? demoSourceIds.includes(contact.source_id) : true))
                  .map((contact) => ({
                    personId: contact.person_id,
                    matchedLocationId: contact.weave_locations_matched[0]?.location_id ?? '',
                    callerName: getDisplayName({
                      firstName: contact.first_name,
                      lastName: contact.last_name,
                      preferredName: contact.preferred_name,
                    }).fullName,
                    callerNumber: payload.params.caller_id_number,
                    recipientLocationName: payload.params.recipient_location_name,
                    gender: contact.gender,
                    birthdate: contact.birthdate.seconds,
                    source: contact.data_source_name,
                    patientId: contact.patient_id,
                    householdId: contact.household_id,
                    pets: contact.pets,
                  }))
              : [
                  {
                    personId: '',
                    patientId: '',
                    householdId: '',
                    // An empty caller name will show "Unknown Caller" in the call pop
                    callerName: getDisplayName({
                      firstName: '',
                      lastName: '',
                      preferredName: '',
                    }).fullName,
                    callerNumber: payload.params.caller_id_number,
                    recipientLocationName: payload.params.recipient_location_name,
                    gender: '',
                    birthdate: 0,
                    matchedLocationId: payload.params.recipient_location_id ?? '',
                    source: '',
                    pets: [],
                  },
                ];

            const photoData = await handleGetPicture(contacts[0].personId);

            addNotification({
              id: payload.params.channel_id,
              timestamp: Date.now(),
              payload: {
                type: 'default',
                autoShowCallerProfile: !!autoShowCallerProfile,
                callerContext: payload.params.caller_context,
                recipientLocationName: payload.params.recipient_location_name,
                headOfHousehold: payload.params.call_pop_head_of_household,
                officeNumberName: payload.params.office_number_name ?? '',
                officeNumber: payload.params.office_number ?? '',
                contacts,
                photoData,
              },
            });
            return;
          }
          case 'answer_call': {
            if (autoShowCallerProfile) {
              const notification = allNotifications?.find((n) => n.id === payload.params.channel_id);
              if (notification) {
                if (!!traceId && isCallPopTracingEnabled) {
                  TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'), {
                    eventMessage: `Call pop auto-opening caller profile on: ${event}`,
                    eventType: EventType.EVENT_TYPE_INFO,
                    timestamp: new Date().toISOString(),
                  });
                }
                CallPopInterface.inspect(notification);
              }
            }
            return;
          }
          case 'outbound_call':
          case 'hangup_call':
            if (!!currentInboundTrace.current) {
              traceId = currentInboundTrace.current;
            }
            if (!!traceId && isCallPopTracingEnabled) {
              TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'), {
                eventMessage: `Call pop dismissed with action: ${event}`,
                eventType: EventType.EVENT_TYPE_INFO,
                timestamp: new Date().toISOString(),
              });
            }
            // Remove the call pop for the call that just ended
            CallPopInterface.dismiss(payload.params.channel_id);

            if (!!traceId && isCallPopTracingEnabled) {
              TempoTracing.endSpan(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'));
              currentInboundTrace.current = undefined;
            }
            return;
          default: {
            const _exhaustive = event;
            return _exhaustive;
          }
        }
      } catch (err) {
        sentry.error({
          topic: 'notification',
          team: 'fe-phones',
          error: err,
          addContext: {
            name: 'notification',
            context: {
              errMessage: 'error during websocket processing in desktop phone, failed to handle',
            },
          },
        });
        console.error(err);
        if (!!traceId && isCallPopTracingEnabled) {
          TempoTracing.addEvent(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'), {
            eventMessage: `Call pop websocket event not handled in deskphone handler due to error: ${err}`,
            eventType: EventType.EVENT_TYPE_INFO,
            timestamp: new Date().toISOString(),
          });
          TempoTracing.endSpan(traceId, TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'));
        }
        return;
      }
    },
    [
      notifications,
      handleGetPicture,
      addNotification,
      autoShowCallerProfile,
      enabled,
      demoSourceIds,
      isCallPopTracingEnabled,
    ]
  );

  /**
   * The dismiss button doesn't do anything. Taking this event out to remove confusion.
   * Note that we need to endSpan here if we want to isolate the span to the dismiss action, or the hangup_call event will come after.
   */
  // const callPopActionHandler: CallPopActionHandler<'dismiss'> = useCallback(() => {
  //   if (!!currentInboundTrace.current) {
  //     TempoTracing.addEvent(
  //       currentInboundTrace.current,
  //       TempoTracing.spanNameGenerators.callPopDesktopSpan('inbound_call'),
  //       {
  //         eventMessage: `User dismissed call using deskphone call pop notification. If a hangup_call event follows this one, then that is the real reason the call ended.`,
  //         eventType: EventType.EVENT_TYPE_INFO,
  //       }
  //     );
  //   }
  // }, []);

  // useSubscribeToPopAction('dismiss', callPopActionHandler);

  useWebsocketEventSubscription('PhoneSystemEventsV2', handlerV2);
};

export const useWebsocketCallQueueEventSubscription = () => {
  const { metrics, setMetrics } = useCallQueueMetricsStore();
  useWebsocketEventSubscription('PhoneCallQueueEvents', (payload) => {
    const event = payload.params;

    setMetrics({
      [event.queue_id]: {
        callCount: event.call_count,
        averageWaitTimeInSeconds: event.average_wait_time_in_seconds,
        waitTimesInSeconds: event.wait_times_in_seconds,
        name: metrics?.[event.queue_id]?.name ?? '',
        locationIds: metrics?.[event.queue_id]?.locationIds ?? [],
      },
    });
  });
};

export const useWebsocketCallRecordEventSubscription = () => {
  const { handleCallRecordEvent, handleCallRecordTagEvent } = useCallRecordEvent();

  useWebsocketEventSubscription(NotificationType.CALL_RECORD, (payload) => {
    const event = payload.params;

    if (window.location.href.includes('calls/call-history')) {
      // update the call records only if the user is on the recent calls page.
      // otherwise ignore the event
      handleCallRecordEvent(event);
    }
  });

  useWebsocketEventSubscription(NotificationType.CALL_RECORD_TAG, (payload) => {
    const event = payload.params;

    if (window.location.href.includes('calls/call-history')) {
      // update the call record tag only if the user is on the recent calls page.
      handleCallRecordTagEvent(event);
    }
  });
};

export const useWebsocketExportCallRecords = () => {
  const { handleExportCallRecordUpdate } = useExportCallRecordUpdater();

  useWebsocketEventSubscription('CALL_RECORD_EXPORT', (payload) => {
    const { id, downloadLink, status } = payload.params;

    handleExportCallRecordUpdate({
      id,
      downloadLink,
      status,
    });
  });
};

/**
 * Global voicemail event subscription that republishes events through an independent emitter.
 * This should be called from the global websocket subscriptions.
 * Components can subscribe to voicemail events using useVoicemailEventSubscription.
 */
export const useWebsocketVoicemailEventSubscription = () => {
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const voicemailEmitter = useVoicemailEventEmitter();

  /**
   * Process voicemail events and emit processed data through the emitter
   */
  useWebsocketEventSubscription('VOICEMAIL', (payload) => {
    const event = payload.params;

    // Handle global concerns - update the unread voicemail count in the navbar
    updateVoicemailUnreadCount(event, selectedLocationIds, phoneConfig?.sipProfileId ?? '', queryClient);

    voicemailEmitter.emit('voicemail:update', { event });
  });

  useWebsocketEventSubscription(NotificationType.VOICEMAIL_TAG, (payload) => {
    const event = payload.params;

    voicemailEmitter.emit('voicemail:tag', { event });
  });
};

// This function updates the unread voicemail count in the navbar
const updateVoicemailUnreadCount = (
  event: VoicemailUpdateEvent,
  selectedLocationIds: string[],
  sipProfileId: string,
  queryClient: QueryClient
) => {
  const voicemailUnReadCountPerMailbox = queryClient.getQueryData([
    selectedLocationIds,
    ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(sipProfileId),
  ]) as CountUnreadVoicemailsResponse;

  const newData = produce(voicemailUnReadCountPerMailbox, (draft) => {
    if (!draft?.countPerMailbox) {
      draft.countPerMailbox = {};
    }
    if (!draft.countPerMailbox[event.mailboxId]) {
      // initialize mailbox unread vm count to 0, if it doesn't exist
      draft.countPerMailbox[event.mailboxId] = 0;
    }

    switch (event.type) {
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_NEW:
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_UNREAD: {
        draft.countPerMailbox[event.mailboxId] = draft.countPerMailbox[event.mailboxId] + 1;
        break;
      }
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_READ: {
        const newCount = draft.countPerMailbox[event.mailboxId] - 1;
        if (newCount < 0) {
          // if the count is invalid, don't update the count. Ideally this shouldn't
          // be happening but just in case
          break;
        }
        draft.countPerMailbox[event.mailboxId] = newCount;

        break;
      }
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_DELETE: {
        if (event.voicemail?.markedRead) {
          // if already read voicemail is deleted, then don't update the count
          break;
        }
        const newCount = draft.countPerMailbox[event.mailboxId] - 1;
        if (newCount < 0) {
          break;
        }
        draft.countPerMailbox[event.mailboxId] = newCount;
        break;
      }
      case VoicemailEventType.VOICEMAIL_EVENT_TYPE_UNSPECIFIED:
        console.error('unspecified voicemail event received');
        return;
    }
  });

  // update the unread voicemail count. This updates the count in navbar
  queryClient.setQueryData(
    [selectedLocationIds, ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(sipProfileId)],
    newData
  );
};
